import {
  runBulk,
  type BulkAction,
} from "@/components/molecules/tables/bulk-actions";
import { useUser } from "@/features/auth/hooks/use-user";
import { useRemoveMember } from "@/features/clients/hooks/use-clients";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import type { ClientMemberWithUser } from "@/features/clients/types";
import { useTranslation } from "@/features/utils/i18n";
import { TrashIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

export const useMembersBulkActions = (): BulkAction<ClientMemberWithUser>[] => {
  const { t } = useTranslation("members");
  const { company: clientId } = useParams<{ company: string }>();
  const isOwner = useHasRole("OWNER");
  const removeMember = useRemoveMember();

  return useMemo(() => {
    if (!isOwner || !clientId) return [];

    return [
      {
        key: "remove",
        label: t("actions.removeSelected"),
        icon: <TrashIcon className="h-4 w-4" />,
        variant: "destructive",
        confirm: (rows) =>
          t("messages.bulkRemoveConfirm", { count: rows.length }),
        onAction: async (rows) => {
          await runBulk(
            rows,
            // Off the team, not out of the tenant. Revoking a login is what
            // the Utilisateurs page is for, and most of these rows have none.
            (member) =>
              removeMember.mutateAsync({
                clientId,
                memberId: member.membership.id,
              }),
            {
              success: (count) => t("messages.bulkRemoveSuccess", { count }),
              error: (count) => t("messages.bulkRemoveError", { count }),
            },
          );
        },
      },
    ];
  }, [isOwner, clientId, t, removeMember]);
};

/**
 * Everybody but yourself.
 *
 * It used to also require an attached account, which quietly made every member
 * without a login unselectable — the performers synced from the booking
 * provider, and anyone added to the team by name. They are exactly the rows this
 * batch is most useful on.
 *
 * Your own row stays out because taking yourself off the team from a batch is a
 * surprise, and the single-row action says it plainly.
 */
export const useCanSelectMember = () => {
  const { data: user } = useUser();
  return useMemo(
    () => (member: ClientMemberWithUser) =>
      !member.user?.id || member.user.id !== user?.id,
    [user?.id],
  );
};
